import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Send as SendIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../context/ToastContext';
import usePermissions from '../../hooks/usePermissions';
import useStorePlan from '../../hooks/useStorePlan';
import { Permissions } from '../../constants/Permissions';
import ConsentFormService from '../../services/consentFormService';
import { formatUtcDateTime } from '../../utils/dateFormatUtils';
import { getConsentFormErrorKey, getConsentFormName } from './consentFormDisplay';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

export const getConsentRequestFeedbackKey = (result) => {
  if (result.pendingCount === 0) {
    return 'consentForms.staff.nothingPending';
  }

  if (result.emailSent && result.smsSent) {
    return 'consentForms.staff.sentEmailAndSms';
  }

  if (result.emailSent) {
    return 'consentForms.staff.sent';
  }

  return result.smsSent ? 'consentForms.staff.sentSms' : 'consentForms.staff.notSent';
};

/**
 * Shows staff whether the customer accepted the consent forms this appointment's services ask
 * for, and lets them send a link when some are still pending. Bookings are never blocked.
 */
const AppointmentConsentStatus = ({ storeId, appointmentId, customerId }) => {
  const { t, i18n } = useTranslation();
  const { showError, showSuccess, showInfo } = useToast();
  const { hasPermission } = usePermissions(storeId);
  const { entitlements } = useStorePlan(storeId, Boolean(storeId));
  const enabled = Boolean(storeId && appointmentId && customerId)
    && hasPermission(Permissions.ConsentFormsRead)
    && Boolean(entitlements?.allowsConsentForms);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      setStatus(await ConsentFormService.getAppointmentConsentStatus(storeId, appointmentId));
    } catch (loadError) {
      console.error('Failed to load consent status:', loadError);
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [appointmentId, storeId]);

  useEffect(() => {
    if (enabled) {
      loadStatus();
    } else {
      setStatus(null);
    }
  }, [enabled, loadStatus]);

  const handleSend = async () => {
    try {
      setSending(true);
      const result = await ConsentFormService.sendConsentRequest(storeId, { customerId, appointmentId });
      const key = getConsentRequestFeedbackKey(result);
      if (result.emailSent || result.smsSent) {
        showSuccess(t(key));
      } else {
        showInfo(t(key));
      }
    } catch (sendError) {
      console.error('Failed to send consent request:', sendError);
      showError(t(getConsentFormErrorKey(sendError, 'consentForms.errors.sendRequest')));
    } finally {
      setSending(false);
    }
  };

  if (!enabled) {
    return null;
  }

  if (loading && !status) {
    return <CircularProgress size={18} sx={{ color: BRAND.accent, mt: 1.5 }} />;
  }

  if (!status || (status.pending.length === 0 && status.accepted.length === 0)) {
    return null;
  }

  const pendingCount = status.pending.length;

  return (
    <Box id="appointment-consent-status" sx={{ mt: 1.75, pt: 1.5, borderTop: `1px solid ${brandAlpha('accent', 0.14)}` }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND.text, mb: 1 }}>
        {t('consentForms.staff.sectionTitle')}
      </Typography>
      <Stack spacing={1}>
        {status.pending.map((form) => (
          <Box key={form.consentFormId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon fontSize="small" sx={{ color: '#ed6c02' }} />
            <Typography variant="body2" sx={{ color: '#4b5563' }}>
              {getConsentFormName(form.name, i18n.language, t)}
            </Typography>
          </Box>
        ))}
        {status.accepted.map((form) => (
          <Box key={form.consentFormId} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <CheckCircleIcon fontSize="small" sx={{ color: '#2e7d32' }} />
            <Typography variant="body2" sx={{ color: '#4b5563' }}>
              {getConsentFormName(form.name, i18n.language, t)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('consentForms.staff.acceptedOn', { date: formatUtcDateTime(form.acceptedAtUtc) })}
            </Typography>
          </Box>
        ))}
      </Stack>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1.25 }}>
        <Chip
          id="appointment-consent-status-chip"
          size="small"
          label={pendingCount > 0 ? t('consentForms.staff.pending', { count: pendingCount }) : t('consentForms.staff.accepted')}
          sx={{
            backgroundColor: pendingCount > 0 ? '#fff4e5' : '#e8f5e9',
            color: pendingCount > 0 ? '#b45309' : '#2e7d32',
            fontWeight: 700,
          }}
        />
        {pendingCount > 0 ? (
          <Button
            id="appointment-consent-send"
            size="small"
            startIcon={sending ? <CircularProgress size={14} /> : <SendIcon fontSize="small" />}
            onClick={handleSend}
            disabled={sending}
            sx={{ color: BRAND.accentText, textTransform: 'none', fontWeight: 600 }}
          >
            {sending ? t('consentForms.staff.sending') : t('consentForms.staff.sendLink')}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};

export default AppointmentConsentStatus;
